import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { isEmpty } from "lodash";
import { getDeploymentStatus, getDeploymentStatuses } from "shared/selects/deployment_statuses";
import { allDeploymentStatusesStore } from "../../../stores/deployment_statuses/all_deployment_statuses_store";
import { deploymentStatusStore } from "../../../stores/deployment_status_store";
import { useStore } from "stimulus-store";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { updateSelect } from "shared/selects/select_helpers";

// Connects to data-controller="selects--deployment-statuses--deployment-status-select"
export default class extends Controller {

    static stores = [
        allDeploymentStatusesStore,
        deploymentStatusStore
    ]

    static values = {
        selected: String,
        updateStore: { type: Boolean, default: true }
    }

    connect() {
        useStore(this);
    }

    initialize() {
        this._populateSelect();
    }

    _populateSelect() {
        const selectedDeploymentStatusId = this.selectedValue;

        getDeploymentStatuses().then((results) => {
            const control = new TomSelect(this.element, {
                placeholder: 'Select deployment status',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'name',
                searchField: 'name',
                sortField: {
                    field: "name",
                    direction: "asc"
                },
                onChange: (event) => this._deploymentStatusSelectionChanged(event),
                onDelete: (event) => this._deploymentStatusUnselectedEvent(event),
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: true // This closes the select after a selection is made.
            });

            // Save the deployment statuses
            this.setAllDeploymentStatusesStoreValue(results);

            // Add the expected options to the select. We're doing it this way instead of
            // in the constructor so the items appear in the underlying select. This makes
            // it possible to test using capybara matchers
            updateSelect(this.element.id, results);

            // Syncing will ensure options are stubbed out within the select element. This
            // ensures the tests will have the data in the DOM it can test against
            control.sync();

            // If a deployment status is already selected, select it!
            if (!isEmpty(selectedDeploymentStatusId)) {
                // Setting silent to true suppresses change events
                // https://tom-select.js.org/docs/api/
                control.setValue(selectedDeploymentStatusId, DO_NOT_EMIT_EVENTS);

                // Find the selected deployment status data and update store if enabled
                const selectedStatus = results.find(status => status.id === selectedDeploymentStatusId);
                if (selectedStatus && this.updateStoreValue) {
                    this.setDeploymentStatusStoreValue(selectedStatus);
                }
            }
        }).catch((error) => {
            // The request can be aborted mid-flight during Turbo Drive navigation,
            // which rejects the promise. Mirror the supplier controller: log and clear the store.
            console.error('Error populating deployment status select:', error);

            if (this.updateStoreValue) {
                this.setDeploymentStatusStoreValue({});
            }
        });
    }

    _deploymentStatusSelectionChanged(deploymentStatusId) {
        // We only want to process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(deploymentStatusId)) {
            return;
        }

        // Fetch the deployment status data and update the store if enabled
        if (this.updateStoreValue) {
            getDeploymentStatus(deploymentStatusId).then((deploymentStatus) => {
                if (deploymentStatus && deploymentStatus.id) {
                    // Update the store with the deployment status data
                    this.setDeploymentStatusStoreValue(deploymentStatus);
                }
            }).catch((error) => {
                // Handle the rejection so it doesn't become an unhandled promise rejection
                // (see _populateSelect for why that causes flaky driver-level errors in tests).
                console.error('Error fetching selected deployment status:', error);
            });
        }

        // Dispatch event for backward compatibility and device configuration updates
        window.dispatchEvent(new CustomEvent('deploymentStatusSelectionMade', {
            detail: {
                deploymentStatusId: deploymentStatusId,
                element: this.element,
                deviceId: this.element.dataset.deviceId,
                property: this.element.dataset.property
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));
    }

    _deploymentStatusUnselectedEvent() {
        // Clear the deployment status store if enabled
        if (this.updateStoreValue) {
            this.setDeploymentStatusStoreValue({});
        }

        // Dispatch event for backward compatibility and device configuration updates
        window.dispatchEvent(new Event('deploymentStatusSelectionCleared', {
            detail: {
                element: this.element,
                deviceId: this.element.dataset.deviceId,
                property: this.element.dataset.property
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));
    }
}